// AjoutAdministrateur.jsx
import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  message,
  Row,
  Col,
  Breadcrumb,
  Select,
  Checkbox,
  Divider,
  Spin,
  Grid,
} from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { UserService } from '../../common/services';

const ROLE_LABELS = {
  directeur: 'Directeur (tout accès)',
  support: 'Support / Développeur (tout accès)',
  comptable: 'Comptable (finances / stats)',
  commercial: 'Commercial (véhicules, chauffeurs, assistance)',
  admin: 'Admin (générique)',
};
const prettyRole = (r) => ROLE_LABELS[r] || (r?.charAt(0).toUpperCase() + r?.slice(1));

const AjoutAdministrateur = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);                 // [{value,label}]
  const [rolePresets, setRolePresets] = useState({});     // { role: ['perm', ...] }

  const [role, setRole] = useState();                     // 'admin' | 'directeur' | ...
  const [selectedPerms, setSelectedPerms] = useState([]); // perms cochées

  const [permList, setPermList] = useState([]);           // [{key,label,group,groupLabel}]
  const [groupOrder, setGroupOrder] = useState([]);       // ['users','partners',...]
  const [groupLabels, setGroupLabels] = useState({});     // {users:'Gestion des accès',...}

  // 🔹 Breakpoints pour responsive
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;                     // < 576px
  const controlSize = isMobile ? 'small' : 'middle';

  // Charge permissions & presets depuis l’API
  useEffect(() => {
    (async () => {
      try {
        const permPayload = await UserService.getPermissions();
        const permsData = permPayload?.data ?? permPayload;

        const list = Array.isArray(permsData?.list)
          ? permsData.list
          : (permsData?.all || []).map(k => ({ key: k, label: k, group: null, groupLabel: null }));

        setPermList(list);
        setGroupLabels(permsData?.groupLabels || {});

        // ordre des groupes (uniques, non nuls)
        const order = [...new Set(list.map(i => i.group).filter(Boolean))];
        setGroupOrder(order);

        const presetPayload = await UserService.getRolePresets();
        const presetsData = presetPayload?.data ?? presetPayload;
        setRolePresets(presetsData || {});

        const roleKeys = Object.keys(presetsData || {});
        const defaultRole = roleKeys.includes('admin') ? 'admin' : roleKeys[0];
        setRole(defaultRole);
        setSelectedPerms((presetsData && presetsData[defaultRole]) || []);

        setRoles(roleKeys.map(r => ({ value: r, label: prettyRole(r) })));
        form.setFieldsValue({
          role: defaultRole,
          permissions: (presetsData && presetsData[defaultRole]) || [],
        });
      } catch (e) {
        console.error(e);
        message.error('Impossible de charger les permissions et rôles.');
      } finally {
        setLoading(false);
      }
    })();
  }, [form]);

  const handleRoleChange = (r) => {
    const next = rolePresets?.[r] || [];
    setRole(r);
    setSelectedPerms(next);
    form.setFieldsValue({ role: r, permissions: next });
  };

  const handleSubmit = async (values) => {
    const fullPhone = `+225${values.phone}`;
    const finalValues = {
      ...values,
      phone: fullPhone,
      role,
      permissions: values.permissions?.length ? values.permissions : (rolePresets?.[role] || []),
    };

    try {
      await UserService.enregistrerUnAdministrateur(finalValues);
      message.success('Administrateur ajouté avec succès');
      navigate('/dashboard/gestion-admins');
    } catch (error) {
      console.error(error);
      message.error("Erreur lors de l'ajout de l'administrateur");
    }
  };

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="row">
          <div className="col-md-12">
            <Breadcrumb
              separator={<RightOutlined style={{ fontSize: 16, color: '#999', fontWeight: 'bolder' }} />}
              style={{ marginBottom: 16 }}
            >
              <Breadcrumb.Item>
                <Link to="/dashboard" style={{ fontSize: 16, fontWeight: 'bolder' }}>🏠 ACCUEIL</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <span style={{ fontSize: 16, fontWeight: 'bolder' }}>👥 AJOUT D'ADMINISTRATEUR</span>
              </Breadcrumb.Item>
            </Breadcrumb>

            <div className="card mb-4">
              <div className="card-body">
                {loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                    <Spin tip="Chargement des permissions..." />
                  </div>
                ) : (
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    size={controlSize}
                    initialValues={{ role, permissions: selectedPerms }}
                  >
                    {/* 🧑‍💼 Infos principales — 1 colonne en mobile, 2 en md, 3 en lg */}
                    <Row gutter={[12, 12]}>
                      <Col xs={24} md={12} lg={8}>
                        <Form.Item
                          name="firstName"
                          label="Nom"
                          rules={[{ required: true }]}
                          normalize={(v) => v?.toUpperCase()}
                        >
                          <Input placeholder="Ex : AMIDOU" style={{ textTransform: 'uppercase' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} lg={8}>
                        <Form.Item
                          name="lastName"
                          label="Prénom"
                          rules={[{ required: true }]}
                          normalize={(v) => v?.toUpperCase()}
                        >
                          <Input placeholder="Ex : BERMAN" style={{ textTransform: 'uppercase' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} lg={8}>
                        <Form.Item
                          name="phone"
                          label="Téléphone"
                          rules={[
                            { required: true },
                            { pattern: /^[0-9]{10}$/, message: 'Le numéro doit contenir 10 chiffres' },
                          ]}
                        >
                          <Input addonBefore="+225" maxLength={10} placeholder="0102030405" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Divider style={{ margin: isMobile ? '8px 0 12px' : '16px 0' }} />

                    {/* 🔐 Rôle + Permissions — rôle en 1/3, permissions en 2/3 (plein écran sur mobile) */}
                    <Row gutter={[12, 12]}>
                      <Col xs={24} md={8}>
                        <Form.Item name="role" label="Rôle" rules={[{ required: true }]}>
                          <Select
                            options={roles}
                            onChange={handleRoleChange}
                            value={role}
                            disabled={roles.length === 0}
                            allowClear={false}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={16}>
                        <Form.Item name="permissions" label="Permissions">
                          <Checkbox.Group
                            style={{ width: '100%' }}
                            value={selectedPerms}
                            onChange={(vals) => {
                              setSelectedPerms(vals);
                              form.setFieldsValue({ permissions: vals });
                            }}
                          >
                            {/* Groupes connus dans l’ordre */}
                            {groupOrder.map((g) => {
                              const items = permList.filter(p => p.group === g);
                              if (!items.length) return null;
                              return (
                                <div key={g} style={{ marginBottom: 12 }}>
                                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                                    {groupLabels?.[g] || g}
                                  </div>
                                  {/* Grille responsive : 1 col en mobile, 2 en sm, 3 en md+ */}
                                  <Row gutter={[8, 8]}>
                                    {items.map(p => (
                                      <Col key={p.key} xs={24} sm={12} md={8}>
                                        <Checkbox value={p.key}>{p.label}</Checkbox>
                                      </Col>
                                    ))}
                                  </Row>
                                </div>
                              );
                            })}

                            {/* Permissions sans groupe (fallback) */}
                            {permList.some(p => !p.group) && (
                              <div style={{ marginTop: 12 }}>
                                <div style={{ fontWeight: 600, marginBottom: 6 }}>Autres</div>
                                <Row gutter={[8, 8]}>
                                  {permList.filter(p => !p.group).map(p => (
                                    <Col key={p.key} xs={24} sm={12} md={8}>
                                      <Checkbox value={p.key}>{p.label}</Checkbox>
                                    </Col>
                                  ))}
                                </Row>
                              </div>
                            )}
                          </Checkbox.Group>
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* ✅ Bouton plein-largeur sur mobile */}
                    <Row>
                      <Col span={24}>
                        <Form.Item style={{ marginTop: isMobile ? 4 : 8 }}>
                          <Button type="primary" htmlType="submit" size={controlSize} block={isMobile}>
                            Enregistrer
                          </Button>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AjoutAdministrateur;
